/**
 * Crisp chat integration for the web app.
 *
 * We reuse the same Crisp WEBSITE_ID as the mobile app so conversations
 * from all platforms land in one unified inbox, with a single timeline
 * per user (keyed by email). To tell platforms apart in the inbox, we
 * tag every session with `baba-web` and attach platform/plan/user_id
 * to session:data — visible in the conversation sidebar.
 *
 * The default floating launcher is hidden (it obstructs the translator
 * UI). A Support button in the app sidebar calls openCrispChat() to
 * open the panel programmatically.
 */

const WEBSITE_ID = '262a09bd-bee9-4c38-9a4a-5cb59ea364ae';

declare global {
  interface Window {
    $crisp: Array<unknown[]> & { push: (args: unknown[]) => void };
    CRISP_WEBSITE_ID: string;
  }
}

let loaded = false;

/**
 * Inject the Crisp script once. Idempotent — safe to call multiple times.
 * Must run in the browser; a no-op during SSR.
 */
export function loadCrisp(): void {
  if (typeof window === 'undefined' || loaded) return;
  loaded = true;

  window.$crisp = window.$crisp || ([] as unknown as Window['$crisp']);
  window.CRISP_WEBSITE_ID = WEBSITE_ID;

  // Hide the default floating launcher. We render a Support button in
  // the sidebar instead — the launcher covers the TTS controls in the
  // bottom-right of the translator on small screens.
  window.$crisp.push(['safe', true]);
  window.$crisp.push(['do', 'chat:hide']);
  // When the panel is closed, re-hide the launcher instead of showing
  // the default bubble.
  window.$crisp.push(['on', 'chat:closed', () => {
    window.$crisp.push(['do', 'chat:hide']);
  }]);

  const s = document.createElement('script');
  s.src = 'https://client.crisp.chat/l.js';
  s.async = true;
  document.getElementsByTagName('head')[0].appendChild(s);
}

interface IdentifyParams {
  email?: string | null;
  userId?: string | null;
  plan?: string | null;
  isPro?: boolean;
}

/**
 * Attach the signed-in user's identity to the Crisp session. Safe to
 * call before the script has finished loading — Crisp queues pushes
 * on window.$crisp until ready.
 *
 * Platform segment `baba-web` lets you filter the Crisp inbox to just
 * web-sourced conversations. Mobile + extension (if we add them later)
 * would use their own segments.
 */
export function identifyCrispUser({ email, userId, plan, isPro }: IdentifyParams): void {
  if (typeof window === 'undefined') return;
  if (!window.$crisp) return;

  if (email) window.$crisp.push(['set', 'user:email', [email]]);

  const segments: string[] = ['baba-web'];
  if (isPro) segments.push('pro');
  else segments.push('free');
  window.$crisp.push(['set', 'session:segments', [segments]]);

  const data: [string, string][] = [['platform', 'web']];
  if (userId) data.push(['user_id', userId]);
  if (plan) data.push(['plan', plan]);
  data.push(['signed_in', 'true']);
  window.$crisp.push(['set', 'session:data', [data]]);
}

export function openCrispChat(): void {
  if (typeof window === 'undefined' || !window.$crisp) return;
  window.$crisp.push(['do', 'chat:show']);
  window.$crisp.push(['do', 'chat:open']);
}

export function resetCrispSession(): void {
  if (typeof window === 'undefined' || !window.$crisp) return;
  // Called on sign-out so the next visitor isn't fingerprinted as the
  // previous user. Crisp's `session:reset` clears email + session data.
  window.$crisp.push(['do', 'session:reset']);
}
